import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Container from '@components/layout/container/container';
import { useTranslations } from 'next-intl';
import Headline from '@components/atoms/headline/headline';
import ShapedImage from '@components/molecules/shaped-image/shapedImage';
import ArrowLink from '@components/molecules/arrow-link/arrowLink';
import MixedCopy from '@components/molecules/mixed-copy/mixedCopy';
import StyledHeroTeaser from './heroTeaser.style';

const HeroTeaser = ({
  data = {
    featuredContentUseBackground: true,
    featuredContent: {
      introCopy: '',
      uri: '',
      subline: '',
      titleImage: null,
      teaserImage: null,
      gomusEvent: null
    }
  },
  accessibilityDefaultTheme
}) => {
  const t = useTranslations();
  if (!data.featuredContent?.[0]) return null;

  const { title, titleImage, teaserImage, headline, subline, introCopy, uri, gomusEvent } =
    data.featuredContent[0];

  const event = gomusEvent?.[0];

  const pickFirstImageWithUrl = (...candidates) =>
    candidates
      .filter(Boolean)
      .flat()
      .find(img => img?.url) ?? null;

  const usedImage = pickFirstImageWithUrl(
    event?.teaserImage,
    event?.titleImage,
    teaserImage,
    titleImage
  );

  return (
    <StyledHeroTeaser
      colorScheme={data.featuredContentColorTheme}
      useBackground={data.featuredContentUseBackground}
      accessibilityDefaultTheme={accessibilityDefaultTheme}
      aria-label={title || ''}
    >
      <div className="teaser__shape" />
      <div className="teaser__section">
        <Container large>
          <div className="teaser__container">
            <div className="teaser__content">
              {subline && (
                <Headline tag="span" subline className="teaser__subline">
                  {subline}
                </Headline>
              )}
              {(headline || title) && (
                <Headline className="teaser__headline" primary>
                  {headline || title}
                </Headline>
              )}
              {introCopy && (
                <MixedCopy
                  className="teaser__copy"
                  fullWidth
                  html={introCopy.length > 200 ? `${introCopy.substring(0, 200)}...` : introCopy}
                />
              )}
              {uri && (
                <ArrowLink
                  href={uri}
                  srTitle={subline ? `${t('learnMoreAbout')}"${subline}"` : t('learnMore')}
                />
              )}
            </div>
            <div className="teaser__image">
              {usedImage?.url && (
                <ShapedImage
                  imageClassName="teaser__image-item"
                  onlyBackground
                  clipForm="polygon(80% 0, 100% 31%, 78% 95%, 0 100%, 0 0)"
                  shadowSize="xl"
                  shadow="right"
                  colorScheme={data.featuredContentColorTheme}
                  alt={usedImage.alt || title}
                  src={usedImage.url}
                  srcset={usedImage.srcset}
                  sizes="(min-width: 768px) 50vw, 100vw"
                />
              )}
            </div>
          </div>
        </Container>
      </div>
    </StyledHeroTeaser>
  );
};

HeroTeaser.propTypes = {
  data: PropTypes.shape({
    featuredContentColorTheme: PropTypes.string.isRequired,
    featuredContentUseBackground: PropTypes.bool,
    featuredContent: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        subline: PropTypes.string,
        introCopy: PropTypes.string,
        uri: PropTypes.string,
        titleImage: PropTypes.arrayOf({
          url: PropTypes.string,
          height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }),
        teaserImage: PropTypes.arrayOf({
          url: PropTypes.string,
          height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
      })
    )
  }),
  accessibilityDefaultTheme: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
  accessibilityDefaultTheme: state.accessibility.defaultTheme
});

export default connect(mapStateToProps)(HeroTeaser);
